/* ─────────────────────────────────────────────────────────────────────────────
   Normalización de las imágenes del ticket.

   Toda imagen que se sube al ticket pasa por aquí antes de guardarse. Antes se
   guardaba el archivo tal cual (salvo los SVG) y de ahí venía que se vieran
   pixeleadas: un logo de 150 px estirado al ancho del ticket no da para más.

   Se rasteriza sobre fondo blanco y se deja en PNG, porque:
     · la impresora térmica no imprime transparencia, la toma como negro
     · el backend solo sabe decodificar PNG, así que un JPG subido tal cual
       se saltaba en silencio y el ticket salía sin logo
     · reducir de un original grande da mucha mejor calidad que ampliar uno
       chico, así que solo se reduce, nunca se amplía.
   ───────────────────────────────────────────────────────────────────────── */

const ANCHO_IMAGEN_TICKET = 1152;       // el doble del ancho de papel de 80 mm (576 px a 203 dpi)
export const ANCHO_MINIMO_NITIDO = 576; // por debajo de esto se le avisa al usuario
const PESO_MAXIMO_DATAURL = 1_200_000;  // el config completo viaja en un PUT con límite de 5 MB

export function normalizarImagenTicket(file: File): Promise<{ dataUrl: string; ancho: number }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('No se pudo leer el archivo'));
    reader.onload = ev => {
      const original = ev.target?.result as string;
      const img = new Image();
      img.onload = () => {
        const srcW = img.naturalWidth  || ANCHO_IMAGEN_TICKET;
        const srcH = img.naturalHeight || Math.round(ANCHO_IMAGEN_TICKET / 2);
        // Un SVG no tiene resolución propia: se rasteriza directo al ancho objetivo
        const esVectorial = file.type === 'image/svg+xml';

        const dibujar = (ancho: number) => {
          const W = Math.max(1, Math.round(ancho));
          const H = Math.max(1, Math.round(srcH * (W / srcW)));
          const canvas = document.createElement('canvas');
          canvas.width = W; canvas.height = H;
          const ctx = canvas.getContext('2d')!;
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, W, H);
          ctx.drawImage(img, 0, 0, W, H);
          return canvas.toDataURL('image/png');
        };

        let dataUrl = dibujar(esVectorial ? ANCHO_IMAGEN_TICKET : Math.min(srcW, ANCHO_IMAGEN_TICKET));
        // Una foto a 1152 px puede pesar de más; a 576 sigue entrando nítida
        // en el papel y el PUT de configuración no se pasa del límite.
        if (dataUrl.length > PESO_MAXIMO_DATAURL) dataUrl = dibujar(ANCHO_MINIMO_NITIDO);

        resolve({ dataUrl, ancho: esVectorial ? ANCHO_IMAGEN_TICKET : srcW });
      };
      // Formato que el navegador no sabe dibujar: se guarda tal cual, como antes
      img.onerror = () => resolve({ dataUrl: original, ancho: ANCHO_IMAGEN_TICKET });
      img.src = original;
    };
    reader.readAsDataURL(file);
  });
}

export const avisoResolucion = (ancho: number) =>
  ancho < ANCHO_MINIMO_NITIDO
    ? `El archivo mide ${ancho} px de ancho. Para que salga nítido en el ticket conviene uno de al menos ${ANCHO_MINIMO_NITIDO} px.`
    : '';
